const FIRST_RETRY_DELAY_MS = 250;
const SECOND_RETRY_DELAY_MS = 750;
const THIRD_RETRY_DELAY_MS = 1_500;
const RETRY_DELAYS_MS = [
  FIRST_RETRY_DELAY_MS,
  SECOND_RETRY_DELAY_MS,
  THIRD_RETRY_DELAY_MS,
] as const;
const REQUEST_TIMEOUT_STATUS = 408;
const REQUEST_IN_PROGRESS_STATUS = 409;
const TOO_EARLY_STATUS = 425;
const RATE_LIMITED_STATUS = 429;
const SERVER_ERROR_STATUS = 500;
const MAX_TOKEN_IDENTIFIER_LENGTH = 128;
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001f\u007f]/;

type ErrorRecord = {
  code?: unknown;
  data?: unknown;
  message?: unknown;
  name?: unknown;
  response?: { status?: unknown };
  status?: unknown;
  statusCode?: unknown;
};

const statusFrom = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const candidate = error as ErrorRecord;
  const value = candidate.status ?? candidate.statusCode ?? candidate.response?.status;
  return Number.isSafeInteger(value) ? Number(value) : undefined;
};

export class BasisTheoryTokenizationResponseInvalidError extends Error {
  constructor() {
    super('Basis Theory tokenization returned a successful response without a token identifier.');
    this.name = 'BasisTheoryTokenizationResponseInvalidError';
  }
}

export const validateBasisTheoryTokenizationResult = <T extends object>(
  value: unknown,
): T & { id: string } => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new BasisTheoryTokenizationResponseInvalidError();
  }
  const id = (value as { id?: unknown }).id;
  if (
    typeof id !== 'string' ||
    id.length === 0 ||
    id.length > MAX_TOKEN_IDENTIFIER_LENGTH ||
    id !== id.trim() ||
    CONTROL_CHARACTER_PATTERN.test(id)
  ) {
    throw new BasisTheoryTokenizationResponseInvalidError();
  }
  return value as T & { id: string };
};

export const isAmbiguousBasisTheoryTokenizationError = (error: unknown): boolean => {
  if (!error) return false;
  if (error instanceof BasisTheoryTokenizationResponseInvalidError) return true;
  const candidate = error as ErrorRecord;
  const combined = [
    candidate.name,
    candidate.code,
    error instanceof Error ? error.message : candidate.message,
    candidate.data,
  ]
    .map((value) => String(value ?? ''))
    .join(' ')
    .toLowerCase();
  if (
    [
      'timeout',
      'timed out',
      'api is unreachable',
      'failed to fetch',
      'load failed',
      'networkerror',
      'network request failed',
      'connection reset',
      'connection closed',
      'valueresolutionfailure',
      'value resolution failure',
    ].some((pattern) => combined.includes(pattern))
  ) {
    return true;
  }

  const status = statusFrom(error);
  return (
    status === 0 ||
    status === REQUEST_TIMEOUT_STATUS ||
    status === REQUEST_IN_PROGRESS_STATUS ||
    status === TOO_EARLY_STATUS ||
    status === RATE_LIMITED_STATUS ||
    (status !== undefined && status >= SERVER_ERROR_STATUS)
  );
};

export class BasisTheoryTokenizationIndeterminateError extends Error {
  readonly code = 'elements_submit_indeterminate';

  constructor(readonly cause: unknown) {
    super('Secure card tokenization may have completed, but its result could not be verified.');
    this.name = 'BasisTheoryTokenizationIndeterminateError';
  }
}

export async function tokenizeBasisTheoryWithIdempotentRetry<T>(
  operation: () => Promise<T>,
  sleep: (delayMs: number) => Promise<void> = (delayMs) =>
    new Promise((resolve) => globalThis.setTimeout(resolve, delayMs)),
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      if (!isAmbiguousBasisTheoryTokenizationError(error)) throw error;
      lastError = error;
      const delay = RETRY_DELAYS_MS[attempt];
      if (delay === undefined) throw new BasisTheoryTokenizationIndeterminateError(lastError);
      await sleep(delay);
    }
  }
  throw new BasisTheoryTokenizationIndeterminateError(lastError);
}
